import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UsersRound, Copy, Share2 } from "lucide-react";

// Stage 0 (2026-02): event-specific invite-a-friend share, wired to the
// referral mechanics. Opened by the under-minimum pre-event nudges
// (?share=1 links) and by the manual "Invite a friend" button on the event
// page. The share URL is the EVENT page carrying the user's ref code —
// index.js snapshots ?ref= on ANY path at bootstrap, so signups from these
// links attribute exactly like landing-page referrals.
export const InviteToEventDialog = ({ opp, onClose }) => {
  const [code, setCode] = useState(null);

  useEffect(() => {
    api.myReferral().then((d) => setCode(d?.code || "")).catch(() => setCode(""));
  }, []);

  const when = opp.event_date
    ? ` on ${new Date(opp.event_date).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" })}`
    : "";
  const url = `${window.location.origin}/opportunities/${opp.id}${code ? `?ref=${code}` : ""}`;
  const message = `Come join me at "${opp.title}"${when} — sign up here: ${url}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Invite copied — paste it anywhere");
      api.trackReferralShare("event_invite").catch(() => {});
    } catch {
      toast.error("Couldn't copy the invite");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Join me: ${opp.title}`, text: `Come join me at "${opp.title}"${when} on CivicHearth`, url });
        api.trackReferralShare("event_invite").catch(() => {});
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    copy();
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="event-invite-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <div className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-bold flex items-center gap-1.5">
            <UsersRound className="w-4 h-4" /> Invite a friend
          </div>
          <DialogTitle className="font-display text-3xl text-forest tracking-tight">
            Twice the hands, twice the fun
          </DialogTitle>
          <DialogDescription>
            Share this event — anyone who signs up through your link counts as your referral too.
          </DialogDescription>
        </DialogHeader>

        {code !== null && (
          <div data-testid="event-invite-message" className="rounded-xl border border-forest/15 bg-forest/5 px-4 py-3 text-sm text-forest break-all">
            {message}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="event-invite-share-btn" onClick={share}
            className="rounded-full bg-forest hover:bg-forest-hover text-white h-10 px-5">
            <Share2 className="w-4 h-4 mr-1.5" /> Share invite
          </Button>
          <Button data-testid="event-invite-copy-btn" variant="outline" onClick={copy}
            className="rounded-full h-10 border-forest/20 text-forest hover:bg-forest/5">
            <Copy className="w-4 h-4 mr-1.5" /> Copy
          </Button>
          <Button data-testid="event-invite-close-btn" variant="ghost" onClick={onClose}
            className="rounded-full ml-auto text-neutral-500 hover:text-neutral-700">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
